<template>
  <a-input
    v-bind="passthroughAttrs"
    :value="innerVal"
    @update:value="onUpdate"
  >
    <template
      v-for="(slotContent, slotName) in $slots"
      :key="slotName"
      #[slotName]="slotProps"
    >
      <slot :name="slotName" v-bind="slotProps" />
    </template>
  </a-input>
</template>

<script setup lang="ts">
import { debounce } from 'lodash';
import { computed, onBeforeUnmount, ref, useAttrs, watch } from 'vue';

interface Props {
  /** 绑定值（v-model 或 v-model:value 均可），等价于 a-input 的 value */
  value?: string;
  modelValue?: string;
  /** 防抖延迟（毫秒），默认 300 */
  debounce?: number;
}

const props = withDefaults(defineProps<Props>(), {
  value: '',
  modelValue: '',
  debounce: 300,
});

const emit = defineEmits<{
  (e: 'update:value', val: string): void;
  (e: 'update:modelValue', val: string): void;
  /** 防抖后触发，等价于 a-input 的 change，供父级监听（如 onLabelInput / onValueConfigChange） */
  (e: 'change', val: string): void;
}>();

// 关闭属性继承：父级传入的 @change / v-model 事件由本组件自行防抖派发，
// 不能透传给内部 a-input，否则每次按键都会直接触发父级 handler（失去防抖意义）。
defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

// 仅透传非事件类属性（allow-clear、size、placeholder、class、style、onClick 等），
// 过滤掉本组件自行管理的 onChange / onUpdate:value / onUpdate:modelValue。
const passthroughAttrs = computed(() => {
  const rest: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'onChange' || k === 'onUpdate:value' || k === 'onUpdate:modelValue') continue;
    rest[k] = v;
  }
  return rest;
});

// 本地即时值：输入时立即回显，保证手感；真正向外派发受 lodash.debounce 控制。
const innerVal = ref<string>((props.modelValue || props.value || '') as string);

// 用 lodash.debounce 实现防抖：仅在停止输入 debounce 毫秒后才向外派发，
// 避免每次按键都触发父级 handler（如 onLabelInput / onValueConfigChange → 接口调用）。
const emitDebounced = debounce((val: string) => {
  emit('update:value', val);
  emit('update:modelValue', val);
  emit('change', val);
}, props.debounce);

// 外部（如父级回填、重置）更新绑定值时，同步内部显示。
watch(
  () => (props.value || props.modelValue || '') as string,
  (next) => {
    if (next !== innerVal.value) innerVal.value = next;
  },
);

const onUpdate = (val: string) => {
  innerVal.value = val;
  emitDebounced(val);
};

onBeforeUnmount(() => {
  emitDebounced.cancel();
});
</script>
